import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  ArrowUp,
  ArrowDown,
  X,
  UploadCloud,
  Eye,
  EyeOff,
} from "lucide-react";
import {
  adminFetchProjects,
  adminCreateProject,
  adminPatchProject,
  adminDeleteProject,
  API,
} from "@/lib/api";
import { adminInputCls, adminBtnCls, adminGoldBtnCls, adminTabCls } from "./shared";

const CATEGORIES = [
  "sheetrock", "finishing", "texture", "insulation", "painting",
  "residential", "commercial", "r3services",
  "dirtWork", "cleanup", "demolition", "hauling",
];

interface Photo {
  original_filename: string;
  storage_path: string;
  status: string;
}

interface Project {
  id: string;
  title: string;
  city: string;
  category: string;
  desc_en: string;
  desc_es: string;
  published: boolean;
  photos: Photo[];
  created_at: string;
}

const emptyForm = {
  title: "",
  city: "",
  category: "sheetrock",
  desc_en: "",
  desc_es: "",
  published: true,
};

export const ProjectsTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState<Record<string, Partial<Project>>>({});

  const load = useCallback(() => {
    adminFetchProjects()
      .then((r) => setProjects(r.data.projects))
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else {
          toast.error("Failed to load projects");
          setProjects([]);
        }
      });
  }, [onAuthError]);

  useEffect(() => load(), [load]);

  const patch = async (id: string, payload: Record<string, unknown>) => {
    try {
      await adminPatchProject(id, payload);
      load();
    } catch (err: any) {
      if (err?.response?.status === 401) onAuthError();
      else toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Project title is required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("city", form.city);
      fd.append("category", form.category);
      fd.append("desc_en", form.desc_en);
      fd.append("desc_es", form.desc_es);
      fd.append("published", String(form.published));
      files.forEach((f) => fd.append("photos", f));
      await adminCreateProject(fd);
      toast.success("Project created");
      setForm(emptyForm);
      setFiles([]);
      setShowForm(false);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Could not create project");
    }
    setSaving(false);
  };

  const movePhoto = (p: Project, idx: number, dir: -1 | 1) => {
    const order = p.photos.map((ph) => ph.storage_path);
    const target = idx + dir;
    if (target < 0 || target >= order.length) return;
    [order[idx], order[target]] = [order[target], order[idx]];
    patch(p.id, { photos_order: order });
  };

  const editFor = (p: Project) => ({
    title: edits[p.id]?.title ?? p.title,
    city: edits[p.id]?.city ?? p.city,
    desc_en: edits[p.id]?.desc_en ?? p.desc_en,
    desc_es: edits[p.id]?.desc_es ?? p.desc_es,
  });

  const setEdit = (p: Project, key: string, value: string) =>
    setEdits((e) => ({ ...e, [p.id]: { ...e[p.id], [key]: value } }));

  return (
    <div data-testid="admin-projects-tab">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-neutral-400">
          Only <span className="font-bold text-gold">published</span> projects appear on the public website.
        </p>
        <button
          data-testid="admin-new-project-btn"
          onClick={() => setShowForm((s) => !s)}
          className={adminGoldBtnCls}
        >
          <Plus className="h-4 w-4" />
          New Project
        </button>
      </div>

      {showForm && (
        <form
          data-testid="admin-project-form"
          onSubmit={create}
          className="mt-6 space-y-5 border border-gold/25 bg-charcoal p-6"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <input
              data-testid="project-title-input"
              required
              placeholder="Project title *"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className={adminInputCls}
            />
            <input
              data-testid="project-city-input"
              placeholder="Project city"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              className={adminInputCls}
            />
            <select
              data-testid="project-category-select"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className={adminInputCls}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-300">
              <input
                data-testid="project-published-checkbox"
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                className="h-4 w-4"
              />
              Published (visible on public site)
            </label>
          </div>
          <textarea
            data-testid="project-desc-en-input"
            rows={2}
            placeholder="English description (optional)"
            value={form.desc_en}
            onChange={(e) => setForm((f) => ({ ...f, desc_en: e.target.value }))}
            className={`${adminInputCls} resize-none`}
          />
          <textarea
            data-testid="project-desc-es-input"
            rows={2}
            placeholder="Descripción en Español (opcional)"
            value={form.desc_es}
            onChange={(e) => setForm((f) => ({ ...f, desc_es: e.target.value }))}
            className={`${adminInputCls} resize-none`}
          />
          <label
            data-testid="project-photos-zone"
            className="flex cursor-pointer flex-col items-center gap-2 border border-dashed border-white/20 bg-ink px-4 py-8 text-center transition-colors duration-300 hover:border-gold/50"
          >
            <UploadCloud className="h-7 w-7 text-gold" strokeWidth={1.5} />
            <span className="text-sm font-bold text-white">
              {files.length ? `${files.length} photo(s) selected` : "Upload project photos (JPG, PNG, WEBP — up to 8)"}
            </span>
            <input
              data-testid="project-photos-input"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp"
              className="sr-only"
              onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 8))}
            />
          </label>
          <button data-testid="project-create-btn" type="submit" disabled={saving} className={adminGoldBtnCls}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Project
          </button>
        </form>
      )}

      <div className="mt-8 space-y-6">
        {projects === null ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : projects.length === 0 ? (
          <p data-testid="admin-projects-empty" className="border border-dashed border-white/15 p-10 text-center text-sm text-neutral-500">
            No projects yet. Add your first real project photos with New Project.
          </p>
        ) : (
          projects.map((p) => (
            <div key={p.id} data-testid={`admin-project-card-${p.id}`} className="border border-white/10 bg-charcoal p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input
                      data-testid={`project-edit-title-${p.id}`}
                      value={editFor(p).title}
                      onChange={(e) => setEdit(p, "title", e.target.value)}
                      className={adminInputCls}
                    />
                    <input
                      data-testid={`project-edit-city-${p.id}`}
                      value={editFor(p).city}
                      onChange={(e) => setEdit(p, "city", e.target.value)}
                      placeholder="City"
                      className={adminInputCls}
                    />
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <textarea
                      data-testid={`project-edit-descen-${p.id}`}
                      rows={2}
                      value={editFor(p).desc_en}
                      onChange={(e) => setEdit(p, "desc_en", e.target.value)}
                      placeholder="English description"
                      className={`${adminInputCls} resize-none`}
                    />
                    <textarea
                      data-testid={`project-edit-desces-${p.id}`}
                      rows={2}
                      value={editFor(p).desc_es}
                      onChange={(e) => setEdit(p, "desc_es", e.target.value)}
                      placeholder="Descripción en Español"
                      className={`${adminInputCls} resize-none`}
                    />
                  </div>
                  <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                    {p.category} · {new Date(p.created_at).toLocaleDateString()} · {p.photos.length} photo(s)
                  </p>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  <select
                    data-testid={`project-edit-category-${p.id}`}
                    value={p.category}
                    onChange={(e) => patch(p.id, { category: e.target.value })}
                    className={`${adminInputCls} w-auto`}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <button
                    data-testid={`project-save-${p.id}`}
                    onClick={() => patch(p.id, editFor(p))}
                    className={adminBtnCls}
                  >
                    <Save className="h-3.5 w-3.5" /> Save
                  </button>
                  <button
                    data-testid={`project-publish-toggle-${p.id}`}
                    onClick={() => patch(p.id, { published: !p.published })}
                    className={adminTabCls(p.published)}
                  >
                    {p.published ? <Eye className="mr-1 inline h-3.5 w-3.5" /> : <EyeOff className="mr-1 inline h-3.5 w-3.5" />}
                    {p.published ? "Published" : "Draft"}
                  </button>
                  <button
                    data-testid={`project-delete-${p.id}`}
                    onClick={() => {
                      if (window.confirm("Delete this project and its photo references?"))
                        adminDeleteProject(p.id).then(load).catch(() => toast.error("Delete failed"));
                    }}
                    className={`${adminBtnCls} hover:border-red-500/60 hover:text-red-400`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>

              {p.photos.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-3">
                  {p.photos.map((ph, i) => (
                    <div key={ph.storage_path} className="group relative h-20 w-28 overflow-hidden border border-white/10">
                      <img
                        src={`${API}/projects/photo/${ph.storage_path}`}
                        alt={ph.original_filename}
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center gap-1 bg-ink/80 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        <button data-testid={`photo-up-${p.id}-${i}`} onClick={() => movePhoto(p, i, -1)} className="p-1 text-neutral-300 hover:text-gold" aria-label="Move earlier">
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`photo-down-${p.id}-${i}`} onClick={() => movePhoto(p, i, 1)} className="p-1 text-neutral-300 hover:text-gold" aria-label="Move later">
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid={`photo-remove-${p.id}-${i}`}
                          onClick={() => patch(p.id, { remove_photos: [ph.storage_path] })}
                          className="p-1 text-neutral-300 hover:text-red-400"
                          aria-label="Remove photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
